/**
 * @file NoteMeetingPicker.tsx
 * @brief Pick which meetings a note is the agenda for — the web twin.
 *
 * @details
 * Mirrors mobile's `NoteMeetingPickerModal`, including the things that are easy
 * to get wrong:
 *
 * 1. **Meetings you can SEE, not only meetings you own.** The list was once
 *    filtered to owned meetings, because a link then required ownership on both
 *    sides and the rest answered `404 Meeting not found`. ADR-024 widened that:
 *    your own note may go on any meeting you were in. `is_owner` still does the
 *    work — it now decides what a row PROMISES rather than whether it appears.
 * 2. **The role is the server's decision.** `consequenceOf` only PREDICTS what
 *    attaching will do, so the user is not tapping blind; the attach response
 *    reports what actually happened, and that is what the toast says. A meeting
 *    can finish processing between the list loading and the click.
 * 3. **Attaching to someone else's meeting asks about visibility first**, since
 *    both intentions are real and publishing by accident cannot be undone.
 *
 * @dependencies
 * - components/ui/PickerSheet
 * - features/company-meetings/api: useCompanyMeetingsList
 * - features/notes/api: useNoteLinks, useAttachNoteToMeeting, useDetach…
 *
 * @references
 * - FRONTEND/src/screens/Notes/NoteMeetingPickerModal.tsx (the mobile twin)
 * - BACKEND/src/features/notes/noteMeetingLinks.service.mjs (the authority)
 * - docs/decisions/ADR-023-note-meeting-links.md
 *
 * @author DES
 * @date 21-08-2026
 */

import { useMemo, useState, type ReactElement } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import { formatDateTime } from "@/shared/utils/formatters";
import { toast } from "@/shared/hooks/useToast";
import { ApiClientError } from "@/shared/api/client";
import { useCompanyMeetingsList } from "@/features/company-meetings/api";
import { useAttachNoteToMeeting, useDetachNoteFromMeeting, useNoteLinks } from "./api";

/**
 * What attaching to a meeting in this state will do.
 *
 * Mirrors the server's `roleForMeetingStatus` / `REFUSED_STATUSES`, which
 * remain the authority. If the two ever disagree the server still decides.
 */
function consequenceOf(status: string | null | undefined, isOwner: boolean): string {
  if (status === "synthesis_processing") {
    return "Processing — try again shortly";
  }
  // On someone else's meeting the answer never depends on status: a
  // participant is clamped to `addendum`, so it is always an attachment.
  if (!isOwner) {
    return "Shared with you · attaches as a note";
  }
  if (status === "ready") {
    return "Already processed · attaches as a note";
  }
  return "Will guide its intelligence";
}

export function NoteMeetingPicker({
  open,
  onOpenChange,
  anchor,
  noteId,
  noteTitle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The button it opens under, as a menu (30-09-2026). */
  anchor?: ReactElement;
  noteId: string;
  noteTitle?: string;
}) {
  // A page of recent meetings is enough to attach from — anything older is
  // reached from the meeting itself. Loaded only while the sheet is open.
  const meetings = useCompanyMeetingsList({ page: 1, pageSize: 50 }, { enabled: open });
  const links = useNoteLinks(noteId, open);
  const attach = useAttachNoteToMeeting();
  const detach = useDetachNoteFromMeeting();

  const { items, ownedIds } = useMemo(() => {
    const all = meetings.data?.meetings ?? [];
    return {
      items: all.map((m) => ({
        id: m.id,
        label: m.title?.trim() || "Untitled meeting",
        subtitle: `${formatDateTime(m.started_at)} · ${consequenceOf(
          m.status,
          m.is_owner !== false,
        )}`,
      })) as PickerSheetItem[],
      // Decides whether attaching asks about visibility, and what the
      // confirmation can promise.
      ownedIds: all.filter((m) => m.is_owner !== false).map((m) => m.id),
    };
  }, [meetings.data]);

  const linkedIds = useMemo(() => (links.data ?? []).map((l) => l.meeting_id), [links.data]);

  const busy = attach.isPending || detach.isPending;
  const [pending, setPending] = useState<PickerSheetItem | null>(null);

  const toggle = (item: PickerSheetItem) => {
    if (busy) return;
    const wasLinked = linkedIds.includes(item.id);

    if (wasLinked) {
      detach.mutate(
        { meetingId: item.id, noteId },
        {
          onError: () =>
            toast({
              variant: "destructive",
              title: "Could not remove",
              description: "Please try again.",
            }),
        },
      );
      return;
    }

    if (!ownedIds.includes(item.id)) {
      onOpenChange(false);
      setPending(item);
      return;
    }
    runAttach(item, false);
  };

  const runAttach = (item: PickerSheetItem, shared: boolean) => {
    attach.mutate(
      { meetingId: item.id, noteId, shared },
      {
        onSuccess: ({ role }) =>
          toast({
            title: role === "prep" ? "Agenda attached" : "Note attached",
            description:
              role === "prep"
                ? `Its points will be checked against ${item.label} when the meeting is processed.`
                : shared
                  ? `Everyone in ${item.label} can read this note. It is not part of the meeting's intelligence.`
                  : `Stored with ${item.label}, visible only to you. You can share it from the meeting.`,
          }),
        onError: (e) =>
          toast({
            variant: "destructive",
            title: "Could not attach",
            // A 409 means the meeting is mid-synthesis, which the user can act
            // on by waiting — swallowing it leaves the row silently unchanged.
            description: e instanceof ApiClientError ? e.message : "Please try again.",
          }),
      },
    );
  };

  return (
    <>
      <PickerSheet
        anchor={anchor}
        open={open}
        onOpenChange={onOpenChange}
        title="Use for a meeting"
        subtitle={noteTitle}
        searchPlaceholder="Search meetings"
        items={items}
        multiSelect
        selectedIds={linkedIds}
        onSelect={toggle}
        loading={meetings.isLoading || links.isLoading}
        clearable={false}
        emptyText="No meetings yet."
      />

      <AlertDialog open={!!pending} onOpenChange={(next) => !next && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Who should see this note?</AlertDialogTitle>
            <AlertDialogDescription>
              You are adding it to “{pending?.label}”, which someone else recorded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (pending) runAttach(pending, false);
                setPending(null);
              }}
            >
              Only me
            </AlertDialogAction>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (pending) runAttach(pending, true);
                setPending(null);
              }}
            >
              Everyone in the meeting
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default NoteMeetingPicker;
